import { z } from "zod/v4";
import { venueIdSchema } from "./ids";

export const venueStatusSchema = z.enum(["draft", "live", "suspended"]);
export const paymentModeSchema = z.enum(["online_required", "online_or_onsite"]);
export const currencySchema = z.enum(["EUR", "USD", "CHF"]);
export const staffRoleSchema = z.enum(["owner", "manager", "staff"]);

// The cancellation policy as the API spells it everywhere (database spelling).
export const policySchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("always_free"), lateRefundBps: z.number().int() }),
	z.object({ kind: z.literal("non_refundable"), lateRefundBps: z.number().int() }),
	z.object({
		freeHours: z.number().int().min(0).max(336),
		kind: z.literal("free_until"),
		lateRefundBps: z.number().int().min(0).max(10_000),
	}),
]);

export const bookingRulesSchema = z.object({
	bufferMinutes: z.number().int(),
	holdMinutes: z.number().int(),
	horizonDays: z.number().int(),
	minLeadMinutes: z.number().int(),
});

export const venueSchema = z.object({
	allowedDurations: z.array(z.number().int().positive()),
	currency: currencySchema,
	id: venueIdSchema,
	locationId: z.uuid().nullable(),
	name: z.string(),
	ownerClubId: z.uuid().nullable(),
	paymentMode: paymentModeSchema,
	policy: policySchema,
	rules: bookingRulesSchema,
	slotStepMinutes: z.union([z.literal(15), z.literal(30), z.literal(60)]),
	slug: z.string(),
	status: venueStatusSchema,
	timezone: z.string(),
	vatRateBps: z.union([z.literal(0), z.literal(700), z.literal(1900)]),
});

/** Slug rule shared with the API: lowercase words joined by single hyphens, 3–60 chars. */
export const venueSlugSchema = z
	.string()
	.min(3)
	.max(60)
	.regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Lowercase letters, digits and single hyphens");

export const signUpVenueSchema = z.object({
	name: z.string().trim().min(1).max(120),
	paymentMode: paymentModeSchema.optional(),
	slug: venueSlugSchema,
});

export const updateVenueSchema = z.object({
	name: z.string().trim().min(1).max(120),
	paymentMode: paymentModeSchema,
	rules: bookingRulesSchema,
});

/** A platform admin provisions a venue for an owner they know by account email. */
export const adminCreateVenueSchema = signUpVenueSchema.extend({
	ownerEmail: z.string().trim().pipe(z.email()),
	paymentMode: paymentModeSchema,
});

/** The identity fields an admin may edit; booking rules stay the owner's. */
export const adminUpdateVenueSchema = updateVenueSchema.omit({ rules: true });

/** The venue's public mini-site profile; null = no description written yet. */
export const venueProfileSchema = z.object({
	description: z.string().nullable(),
});

export const updateVenueProfileSchema = z.object({
	description: z.string().trim().min(1).max(1200).nullable(),
});

/** The venue's Stripe account verdict; absent (404) until the venue creates one. */
export const payoutAccountSchema = z.object({
	chargesEnabled: z.boolean(),
	payoutsEnabled: z.boolean(),
	requirementsDue: z.boolean(),
	stripeAccountId: z.string(),
});

/** Platform-wide figures for the admin's oversight strip. */
export const venueStatsSchema = z.object({
	bookingsLast30Days: z.object({
		cancelled: z.number().int().nonnegative(),
		confirmed: z.number().int().nonnegative(),
	}),
	pendingLocationRequests: z.number().int().nonnegative(),
	venues: z.object({
		draft: z.number().int().nonnegative(),
		live: z.number().int().nonnegative(),
		suspended: z.number().int().nonnegative(),
		unlinked: z.number().int().nonnegative(),
	}),
});

export const memberSchema = z.object({
	email: z.string(),
	role: staffRoleSchema,
	userId: z.uuid(),
});

export type VenueStatus = z.infer<typeof venueStatusSchema>;
export type PaymentMode = z.infer<typeof paymentModeSchema>;
export type Currency = z.infer<typeof currencySchema>;
export type StaffRole = z.infer<typeof staffRoleSchema>;
export type Policy = z.infer<typeof policySchema>;
export type BookingRules = z.infer<typeof bookingRulesSchema>;
export type Venue = z.infer<typeof venueSchema>;
export type SignUpVenue = z.infer<typeof signUpVenueSchema>;
export type UpdateVenue = z.infer<typeof updateVenueSchema>;
export type AdminCreateVenue = z.infer<typeof adminCreateVenueSchema>;
export type AdminUpdateVenue = z.infer<typeof adminUpdateVenueSchema>;
export type VenueProfile = z.infer<typeof venueProfileSchema>;
export type UpdateVenueProfile = z.infer<typeof updateVenueProfileSchema>;
export type VenueMember = z.infer<typeof memberSchema>;
export type PayoutAccount = z.infer<typeof payoutAccountSchema>;
export type VenueStats = z.infer<typeof venueStatsSchema>;
